import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProjectsStore } from '../store/projects-store'
import { ChevronLeft, ChevronRight, AlertCircle, Loader } from 'lucide-react'
import { CreateProjectModal } from '../components/CreateProjectModal'
import { JoinExistingProjectModal } from '../components/JoinExistingProjectModal'

export function ProjectsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false)
  const {
    projects,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalCount,
    hasNext,
    hasPrevious,
    fetchProjects,
    nextPage,
    previousPage,
    goToPage,
    clearError,
  } = useProjectsStore()

  useEffect(() => {
    fetchProjects(1, 50)
  }, [])

  const handlePageChange = (newPage) => {
    goToPage(newPage)
  }

  const handleCreateProjectSuccess = (newProject) => {
    // Refresh projects list to show the new project
    fetchProjects(1, 50)
    // Optionally show success message
    console.log('Project created successfully:', newProject)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Projects</h1>
            <p className="text-gray-600">
              Manage and view all your projects
              {totalCount > 0 && ` (Total: ${totalCount})`}
            </p>
          </div>
          {projects.length > 0 && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 rounded-lg font-semibold transition duration-200 flex items-center gap-2 whitespace-nowrap"
              style={{
                backgroundColor: '#358ECD',
                color: 'white',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2a6fa3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#358ECD'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New
            </button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="text-red-900 font-semibold">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-900 font-semibold"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader className="text-blue-600 animate-spin" size={40} />
          </div>
        )}

        {/* Empty State - No Active Project */}
        {!isLoading && projects.length === 0 && !error && (
          <div className="bg-white rounded-lg shadow-lg p-16">
            <div className="flex flex-col items-center justify-center text-center">
              {/* Icon */}
              <div className="mb-6">
                <svg
                  className="mx-auto h-20 w-20 text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              {/* Message */}
              <h2 className="text-3xl font-bold text-gray-900 mb-2">No Active Project.</h2>
              <p className="text-gray-500 mb-8 text-base">
                You don't have any active projects assigned yet.
              </p>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="px-8 py-3 rounded-lg font-semibold transition duration-200 flex items-center gap-2"
                  style={{
                    backgroundColor: '#358ECD',
                    color: 'white',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2a6fa3'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#358ECD'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New
                </button>
                <button
                  onClick={() => setIsJoinModalOpen(true)}
                  className="px-8 py-3 rounded-lg font-semibold transition duration-200 border-2"
                  style={{
                    borderColor: '#358ECD',
                    color: '#358ECD',
                    backgroundColor: 'white',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f0f7ff'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white'
                  }}
                >
                  Join Existing
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Projects Grid */}
        {!isLoading && projects.length > 0 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">
                  Page <span className="font-semibold">{currentPage}</span> of{' '}
                  <span className="font-semibold">{totalPages}</span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={previousPage}
                    disabled={!hasPrevious}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum =
                        totalPages <= 5
                          ? i + 1
                          : Math.max(1, currentPage - 2) + i
                      if (pageNum > totalPages) return null
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className="px-3 py-2 rounded-lg transition"
                          style={pageNum === currentPage ? {
                            backgroundColor: '#358ECD',
                            color: 'white'
                          } : {
                            border: '1px solid #d1d5db',
                            color: '#374151',
                            backgroundColor: '#ffffff'
                          }}
                          onMouseEnter={(e) => {
                            if (pageNum !== currentPage) {
                              e.target.style.backgroundColor = '#f3f4f6'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (pageNum !== currentPage) {
                              e.target.style.backgroundColor = '#ffffff'
                            }
                          }}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    onClick={nextPage}
                    disabled={!hasNext}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateProjectSuccess}
      />

      {/* Join Existing Project Modal */}
      <JoinExistingProjectModal
        isOpen={isJoinModalOpen}
        onClose={() => setIsJoinModalOpen(false)}
      />
    </div>
  )
}

/**
 * Project Card Component
 */
function ProjectCard({ project }) {
  const navigate = useNavigate()
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition duration-300 overflow-hidden flex flex-col">
      {/* Color Header */}
      <div
        className="h-2"
        style={{
          backgroundColor: project.background_color || '#3b82f6',
        }}
      />

      {/* Project Content */}
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
          {project.name}
        </h3>

        <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">
          {project.description || project.overview || 'No description provided'}
        </p>

        {/* Project Meta */}
        <div className="space-y-2 text-xs text-gray-500 mb-4 pt-4 border-t border-gray-200">
          {project.owner && (
            <div className="flex justify-between">
              <span className="font-semibold">Owner:</span>
              <span>{project.owner.name || project.owner.email}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="font-semibold">Created:</span>
            <span>{formatDate(project.created_at)}</span>
          </div>
          {project.updated_at && (
            <div className="flex justify-between">
              <span className="font-semibold">Updated:</span>
              <span>{formatDate(project.updated_at)}</span>
            </div>
          )}
        </div>

        {/* Project Badges */}
        <div className="flex gap-2 flex-wrap">
          {project.demo && (
            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
              Demo
            </span>
          )}
          {project.publicportal && (
            <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
              Public Portal
            </span>
          )}
          {project.presentation && (
            <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
              Presentation
            </span>
          )}
        </div>

        {/* Action Button — opens this project's dashboard */}
        <button onClick={() => navigate(`/dashboard/project/${project.id}`)} className="mt-4 w-full py-2 text-white font-semibold rounded-lg transition duration-200" style={{backgroundColor: '#358ECD'}} onMouseEnter={(e) => e.target.style.backgroundColor = '#2a6fa3'} onMouseLeave={(e) => e.target.style.backgroundColor = '#358ECD'}>
          View Project
        </button>
      </div>
    </div>
  )
}
